import { call, fork, put } from 'redux-saga/effects'

// Utilities
import defaultTrue from 'fc-utils/defaultTrue'
import required from 'fc-utils/required'
import createState from './state/createState'

// Composers
import composeActionTypes from './actionTypes'
import composeActionCreators from './actionCreators'
import composeWatch from './watchers'
import composeCreate from './sagas/composeCreate'
import composeRead from './sagas/composeRead'
import composeUpdate from './sagas/composeUpdate'
import composeDelete from './sagas/composeDelete'
import composeReducers from './reducers'
import composeWhen from './sagas/when'

// Local
function composeNotInitialized (state) {
  return function *notInitialized () {
    const error = yield new Error(
      `Controller for ${state.get().model} has not been initialized or operation is not permitted.`
    )

    yield call([Promise, Promise.reject], error)
  }
}

export default function createController (maybeCrud = {}) {
  const crud = {
    C: defaultTrue(maybeCrud.C),
    R: defaultTrue(maybeCrud.R),
    U: defaultTrue(maybeCrud.U),
    D: defaultTrue(maybeCrud.D),
  }

  const state = createState()

  state.permitCreate(crud.C)
  state.permitRead(crud.R)
  state.permitUpdate(crud.U)
  state.permitDelete(crud.D)

  return function ({
    url = required('url'),
    model = required('model'),
    tree = required('tree'),
    branch = required('branch'),
    token$ = required('token$: Token selector'),
    watch: _watch,
    initialize: initializeController,
  }) {
    state.initialize({
      url,
      model,
      tree,
      branch,
      watch: _watch,
    })

    const notInitialized = composeNotInitialized(state)

    const actionTypes = composeActionTypes(state)
    const actionCreators = composeActionCreators(state)({actionTypes})
    const reducers = composeReducers(state)({actionTypes, branch})

    const controller = {
      actionTypes,
      actionCreators,
      reducers,
      watch: notInitialized,
      create: notInitialized,
      add: notInitialized,
      read: notInitialized,
      get: notInitialized,
      update: notInitialized,
      edit: notInitialized,
      delete: notInitialized,
      remove: notInitialized,
      when: notInitialized,
      state,
      initialized: false,
    }

    const start = function start () {
      return new Promise((resolve) => {
        initializeController(controller).then(({handleFetch, terminate}) => {
          const { watch, sagas } = composeWatch({
            state,
            actionTypes,
            actionCreators,
            token$,
            handleFetch,
          })

          if (state.permissions().create) {
            const { create, createNonBlocking } = composeCreate({
              state,
              actionCreators,
              handler: sagas.create,
            })

            controller['create'] = create
            controller['add'] = createNonBlocking
          }

          if (state.permissions().read) {
            const { read, readNonBlocking } = composeRead({
              state,
              actionCreators,
              handler: sagas.read,
            })

            controller['read'] = read
            controller['get'] = readNonBlocking
          }

          if (state.permissions().update) {
            const { update, updateNonBlocking } = composeUpdate({
              state,
              actionCreators,
              handler: sagas.update,
            })

            controller['update'] = update
            controller['edit'] = updateNonBlocking
          }

          if (state.permissions().delete) {
            const { delete_, deleteNonBlocking } = composeDelete({
              state,
              actionCreators,
              handler: sagas.delete,
            })

            controller['delete'] = delete_
            controller['remove'] = deleteNonBlocking
          }

          controller['when'] = composeWhen({state, actionTypes})
          controller['watch'] = watch

          controller['terminate'] = function terminateController () {
            controller['terminate'] = Promise.resolve(true)
            console.warn(`Terminating controller for ${state.get().model}`)
            controller['watch'] = notInitialized
            controller['create'] = notInitialized
            controller['add'] = notInitialized
            controller['read'] = notInitialized
            controller['get'] = notInitialized
            controller['update'] = notInitialized
            controller['edit'] = notInitialized
            controller['delete'] = notInitialized
            controller['remove'] = notInitialized
            controller['when'] = notInitialized
            terminate()
          }

          resolve({
            controller,
            terminate,
          })
        })
      })
    }

    controller.start = start
    controller.boot = function *boot (chan) {
      yield call(controller.start)
      yield fork(controller.watch)
      yield put(chan, {
        type: 'OK',
      })
    }

    return controller
  }
}

export const readController = function (...args) {
  return createController({C: false, U: false, D: false})(...args)
}

export const crudController = function (...args) {
  return createController()(...args)
}
